import { computed, reactive, readonly } from 'vue'
import { defineStore } from 'pinia'

export const useAppStore = defineStore('app', () => {
	const state = reactive({
		user: {
			isAuthenticated: false,
			profile: null,
			permissions: [],
		},
		ui: {
			loading: false,
			sidebarOpen: false,
			theme: 'light',
			language: 'ru',
		},
		notifications: [],
		cache: new Map(),
	})

	let notificationId = 0

	const isAuthenticated = computed(() => state.user.isAuthenticated)
	const currentUser = computed(() => state.user.profile)
	const user = currentUser
	const isLoading = computed(() => state.ui.loading)
	const notifications = computed(() => state.notifications)
	const theme = computed(() => state.ui.theme)
	const language = computed(() => state.ui.language)

	const setUser = userProfile => {
		state.user.profile = userProfile
		state.user.isAuthenticated = !!userProfile
	}

	const logout = () => {
		state.user.profile = null
		state.user.isAuthenticated = false
		state.user.permissions = []
		
		// Очищаем все данные авторизации из localStorage
		localStorage.removeItem('app-user')
		localStorage.removeItem('access_token')
		localStorage.removeItem('refresh_token')
		localStorage.removeItem('user')
		localStorage.removeItem('login_time')
		
		clearCache()
	}

	const clearUserData = () => {
		state.user.profile = null
		state.user.isAuthenticated = false
		state.user.permissions = []
		localStorage.removeItem('app-user')
		localStorage.removeItem('token')
		localStorage.removeItem('refreshToken')
		clearCache()
		clearNotifications()
	}

	const setPermissions = permissions => {
		state.user.permissions = permissions || []
	}

	const hasPermission = permission => state.user.permissions.includes(permission)

	const setLoading = loading => {
		state.ui.loading = loading
	}

	const toggleSidebar = () => {
		state.ui.sidebarOpen = !state.ui.sidebarOpen
	}

	const setSidebarOpen = open => {
		state.ui.sidebarOpen = open
	}

	const setTheme = themeValue => {
		state.ui.theme = themeValue
		localStorage.setItem('app-theme', themeValue)
	}

	const setLanguage = languageValue => {
		state.ui.language = languageValue
		localStorage.setItem('app-language', languageValue)
	}

	const addNotification = notification => {
		const id = ++notificationId
		const newNotification = {
			id,
			type: 'info',
			title: '',
			message: '',
			duration: 5000,
			channel: 'toast',
			...notification,
			timestamp: Date.now(),
		}

		state.notifications.push(newNotification)

		if (newNotification.duration > 0 && newNotification.channel === 'toast') {
			setTimeout(() => {
				removeNotification(id)
			}, newNotification.duration)
		}

		return id
	}

	const removeNotification = id => {
		const index = state.notifications.findIndex(n => n.id === id)
		if (index > -1) {
			state.notifications.splice(index, 1)
		}
	}

	const clearNotifications = channel => {
		if (!channel) {
			state.notifications.length = 0
			return
		}
		for (let i = state.notifications.length - 1; i >= 0; i -= 1) {
			const item = state.notifications[i]
			const itemChannel = item?.channel || 'toast'
			if (itemChannel === channel) {
				state.notifications.splice(i, 1)
			}
		}
	}

	const showSuccess = (message, title = 'Success') => {
		return addNotification({
			type: 'success',
			title,
			message,
			duration: 2000,
			channel: 'toast',
		})
	}

	const showError = (message, title = 'Error') => {
		return addNotification({
			type: 'error',
			title,
			message,
			duration: 2000,
			channel: 'toast',
		})
	}

	const showWarning = (message, title = 'Warning') => {
		return addNotification({
			type: 'warning',
			title,
			message,
			duration: 2000,
			channel: 'toast',
		})
	}

	const showInfo = (message, title = 'Info') => {
		return addNotification({
			type: 'info',
			title,
			message,
			duration: 2000,
			channel: 'toast',
		})
	}

	const setCache = (key, value, ttl = 0) => {
		const item = {
			value,
			timestamp: Date.now(),
			ttl,
		}
		state.cache.set(key, item)
	}

	const getCache = key => {
		const item = state.cache.get(key)
		if (!item) return null

		if (item.ttl > 0 && Date.now() - item.timestamp > item.ttl) {
			state.cache.delete(key)
			return null
		}

		return item.value
	}

	const clearCache = () => {
		state.cache.clear()
	}

	const removeFromCache = key => {
		state.cache.delete(key)
	}

	const initializeFromStorage = () => {
		const savedTheme = localStorage.getItem('app-theme')
		if (savedTheme) {
			state.ui.theme = savedTheme
		}

		const savedLanguage = localStorage.getItem('app-language')
		if (savedLanguage) {
			state.ui.language = savedLanguage
		}

		const savedUser = localStorage.getItem('app-user')
		if (savedUser) {
			try {
				const userProfile = JSON.parse(savedUser)
				setUser(userProfile)
			} catch (error) {
				console.error('[AppStore] Failed to parse saved user profile', error)
			}
		}
	}

	const persistUser = () => {
		if (state.user.profile) {
			localStorage.setItem('app-user', JSON.stringify(state.user.profile))
		} else {
			localStorage.removeItem('app-user')
		}
	}

	const setBitrix24Auth = (authData) => {
		const { tokens, user, needsPassword } = authData
		
		// Сохраняем токены (используем те же ключи, что и authService)
		if (tokens) {
			localStorage.setItem('access_token', tokens.accessToken)
			localStorage.setItem('refresh_token', tokens.refreshToken)
			localStorage.setItem('token', tokens.accessToken)
			localStorage.setItem('refreshToken', tokens.refreshToken)
		}

		localStorage.setItem('bitrix24_mode', 'true')
		localStorage.setItem('login_time', String(Date.now()))
		
		// Устанавливаем пользователя
		if (user) {
			setUser(user)
			persistUser()
		}

		if (needsPassword) {
			localStorage.setItem('bitrix24_needs_password', 'true')
			localStorage.removeItem('bitrix24_onboarding_completed')
		} else {
			localStorage.removeItem('bitrix24_needs_password')
		}
		
		// Устанавливаем флаг авторизации
		state.user.isAuthenticated = true
		
		return Promise.resolve(authData)
	}

	return {
		state: readonly(state),

		isAuthenticated,
		currentUser,
		user,
		isLoading,
		notifications,
		theme,
		language,

		setUser,
		setBitrix24Auth,
		logout,
		clearUserData,
		setPermissions,
		hasPermission,

		setLoading,
		toggleSidebar,
		setSidebarOpen,
		setTheme,
		setLanguage,

		addNotification,
		removeNotification,
		clearNotifications,
		showSuccess,
		showError,
		showWarning,
		showInfo,

		setCache,
		getCache,
		clearCache,
		removeFromCache,

		initializeFromStorage,
		persistUser,
	}
})
